/**
 * Typed API client for the Shubh Time NestJS backend.
 *
 * In development, Vite proxies /api → localhost:3000 (stripping the prefix).
 * In production, set VITE_API_URL to the full API origin
 * (e.g. https://api.shubhtime.in — deployed on Railway).
 */

const BASE_URL = import.meta.env.VITE_API_URL || '/api'

// ── Types ─────────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Returns true when a query error is a 404 — used to show a setup CTA
 *  instead of a generic retry banner. */
export function isNotFoundError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  params?: Record<string, string | number | boolean | undefined>
}

// ── Token storage ──────────────────────────────────────────────────────────
// Access token is stored in memory only (XSS-safe, short-lived).
// Refresh token is persisted in localStorage so it survives page reloads.

const REFRESH_TOKEN_KEY = 'st_refresh_token'

let _accessToken: string | null = null

export const tokenStore = {
  get: () => _accessToken,
  set: (token: string | null) => { _accessToken = token },
  getRefresh: (): string | null => {
    try { return localStorage.getItem(REFRESH_TOKEN_KEY) } catch { return null }
  },
  setRefresh: (token: string | null) => {
    try {
      if (token) localStorage.setItem(REFRESH_TOKEN_KEY, token)
      else localStorage.removeItem(REFRESH_TOKEN_KEY)
    } catch { /* SSR / private browsing */ }
  },
  clear: () => {
    _accessToken = null
    try { localStorage.removeItem(REFRESH_TOKEN_KEY) } catch { /* noop */ }
  },
}

// ── Core fetch wrapper ─────────────────────────────────────────────────────

async function request<T>(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, params, headers: extraHeaders, ...rest } = options

  // Build URL with query params
  let url = `${BASE_URL}${path}`
  if (params) {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join('&')
    if (qs) url += `?${qs}`
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...extraHeaders as Record<string, string>,
  }

  // Attach access token if available
  const token = tokenStore.get()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: 'include', // sends httpOnly refresh-token cookie
    ...rest,
  }

  if (body !== undefined) {
    init.body = JSON.stringify(body)
  }

  let response = await fetch(url, init)

  // Auto-refresh on 401
  if (response.status === 401 && path !== '/v1/auth/refresh') {
    try {
      const refreshed = await refreshTokens()
      if (refreshed) {
        // Retry original request with new token
        headers['Authorization'] = `Bearer ${tokenStore.get()}`
        response = await fetch(url, { ...init, headers })
      } else {
        // No refresh token or refresh failed — session is gone
        tokenStore.clear()
        window.dispatchEvent(new Event('auth:logout'))
        throw new ApiError(401, 'UNAUTHENTICATED', 'Session expired')
      }
    } catch (err) {
      if (err instanceof ApiError) throw err
      tokenStore.clear()
      window.dispatchEvent(new Event('auth:logout'))
      throw new ApiError(401, 'UNAUTHENTICATED', 'Session expired')
    }
  }

  if (!response.ok) {
    let code = 'UNKNOWN_ERROR'
    let message = `Request failed with status ${response.status}`
    try {
      const err = await response.json()
      code = err.code ?? err.error ?? code
      message = err.message ?? message
    } catch { /* ignore parse error */ }
    throw new ApiError(response.status, code, message)
  }

  // 204 No Content
  if (response.status === 204) return undefined as T

  return response.json()
}

// ── Token refresh (singleton to prevent concurrent rotation conflicts) ────

let _refreshPromise: Promise<boolean> | null = null

function refreshTokens(): Promise<boolean> {
  if (!_refreshPromise) {
    _refreshPromise = _doRefresh().finally(() => {
      _refreshPromise = null
    })
  }
  return _refreshPromise
}

async function _doRefresh(): Promise<boolean> {
  const refreshToken = tokenStore.getRefresh()
  if (!refreshToken) return false
  const response = await fetch(`${BASE_URL}/v1/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ refreshToken }),
  })
  if (!response.ok) return false
  const data = await response.json() as { accessToken: string; refreshToken: string }
  tokenStore.set(data.accessToken)
  tokenStore.setRefresh(data.refreshToken)
  return true
}

// ── HTTP verbs ─────────────────────────────────────────────────────────────

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>('GET', path, options),

  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, { ...options, body }),

  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, { ...options, body }),

  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, { ...options, body }),

  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, options),
}

// ── Auth endpoints ─────────────────────────────────────────────────────────

export const authApi = {
  googleLogin: () => {
    window.location.href = `${BASE_URL}/v1/auth/google`
  },

  appleLogin: () => {
    window.location.href = `${BASE_URL}/v1/auth/apple`
  },

  logout: () => api.post('/v1/auth/logout'),

  me: () => api.get<User>('/v1/users/me'),

  refresh: refreshTokens,

  sendEmailOtp: (email: string): Promise<void> =>
    api.post('/v1/auth/email/send-otp', { email }),

  verifyEmailOtp: (email: string, otp: string): Promise<{ accessToken: string; refreshToken: string }> =>
    api.post('/v1/auth/email/verify-otp', { email, otp }),
}

// ── Domain types ────────────────────────────────────────────────────────────

export interface UserProfile {
  dateOfBirth?: string | null
  timeOfBirth?: string | null
  birthCity?: string | null
  birthLatitude?: number | null
  birthLongitude?: number | null
  birthTimezone?: string | null
  goals?: string[] | null
}

export interface User {
  id: string
  email: string
  displayName: string
  avatarUrl?: string
  role?: 'user' | 'admin'
  profile?: UserProfile | null
  dashaIndicator?: string  // current dasha period label
  createdAt: string
}

export interface BirthChart {
  id: string
  userId: string
  ascendant: string
  moonSign: string
  sunSign: string
  planets: PlanetPosition[]
  dashas: DashaNode[]
}

export interface PlanetPosition {
  planet: string
  sign: string
  house: number
  degrees: number
  isRetrograde: boolean
  nakshatra: string
}

export interface DashaNode {
  planet: string
  startDate: string
  endDate: string
  level: 'mahadasha' | 'antardasha' | 'pratyantardasha'
  children?: DashaNode[]
}

export interface MuhurtaLayerScore {
  layer: 'gochar' | 'divisional' | 'dasha' | 'ashtakvarga' | 'nakshatra' | 'degrees'
  score: number
  maxScore: number
  label: string
}

export interface MuhurtaSlot {
  id: string
  startTime: string
  endTime: string
  score: number
  quality: 'excellent' | 'good' | 'neutral' | 'avoid'
  reasons: string[]
  category: string
  layers?: MuhurtaLayerScore[]  // detailed six-layer breakdown (when available)
}

// ── Muhurta enriched window type (v4 API, SHUA-81) ────────────────────────────

export interface BilingualText {
  en: string
  hi: string
}

export interface MuhurtaScoreBreakdown {
  panchaanga:      number  // 0-100
  nakshatraFit:    number
  transitStrength: number
  dashaHarmony:    number
  yogas:           number
  lagnaStrength:   number
}

export interface MuhurtaWindowMeta {
  nakshatra:      number   // 1-27
  tithi:          number   // 1-30
  vara:           number   // 0=Sun … 6=Sat
  isRahuKalaFree: boolean
}

export interface MuhurtaWindow {
  startUtc:       string
  endUtc:         string
  score:          number
  qualityLabel:   'excellent' | 'good' | 'fair' | 'poor'
  recommendation: boolean   // true for top 3 windows
  scoreBreakdown: MuhurtaScoreBreakdown
  explanation:    BilingualText
  meta:           MuhurtaWindowMeta
}

// ── Muhurta bookmark ──────────────────────────────────────────────────────────

export interface MuhurtaBookmark {
  id:             string
  windowStartUtc: string
  windowEndUtc:   string
  eventType:      string
  score:          number
  note?:          string
  createdAt:      string
}

export interface CreateBookmarkPayload {
  windowStartUtc: string
  windowEndUtc:   string
  eventType:      string
  score:          number
  note?:          string
}

// ── Chart interpretation (SHUA-77) ────────────────────────────────────────────

export interface ChartPlanetInterpretation {
  graha:       string
  house:       number
  sign:        string
  description: BilingualText
}

export interface ChartYoga {
  name:        string
  description: BilingualText
  planets:     string[]
}

export interface ChartInterpretation {
  chartId:       string
  overview:      BilingualText[]
  lagna:         BilingualText
  moonSign:      BilingualText
  sunSign:       BilingualText
  moonNakshatra: BilingualText
  planetInHouse: ChartPlanetInterpretation[]
  keyYogas:      ChartYoga[]
}

// ── Dasha interpretation (SHUA-79) ────────────────────────────────────────────

export interface DashaInterpretationDomains {
  overallTheme:  BilingualText
  career:        BilingualText
  relationships: BilingualText
  health:        BilingualText
  finances:      BilingualText
  spirituality:  BilingualText
  dignityNote?:  BilingualText
  houseContext?: BilingualText
}

export interface DashaPeriodInterpreted {
  planet:         string
  level:          number   // 1 = Mahadasha, 2 = Antardasha
  startDate:      string
  endDate:        string
  durationDays:   number
  interpretation: DashaInterpretationDomains
}

export interface DashaTimeline {
  chartId: string
  periods: DashaPeriodInterpreted[]
}

export interface DashaTransitionAlert {
  mahadashaEndsInDays:  number
  antardashaEndsInDays: number
  whatToExpect:         BilingualText
}

export interface CurrentDasha {
  currentMahadasha:  DashaPeriodInterpreted
  currentAntardasha: DashaPeriodInterpreted | null
  transitionAlert:   DashaTransitionAlert
}

// ── Choghadiya (SHUA-67) ──────────────────────────────────────────────────────

export type ChoghadiyaQuality = 'Amrit' | 'Shubh' | 'Labh' | 'Char' | 'Udveg' | 'Kaal' | 'Rog'

export interface ChoghadiyaSlot {
  startTime: string   // ISO UTC
  endTime:   string
  quality:   ChoghadiyaQuality
  planet:    string
  period:    'day' | 'night'
}

export interface DailyTimings {
  date:                string
  sunrise:             string
  sunset:              string
  choghadiya:          ChoghadiyaSlot[]
  inauspiciousWindows: Array<{ name: string; start: string; end: string }>
}

export interface SetupProfilePayload {
  displayName: string
  dateOfBirth: string      // YYYY-MM-DD
  timeOfBirth: string      // HH:mm
  birthCity: string
  birthLatitude: number
  birthLongitude: number
  birthTimezone: string
  goals?: string[]
}

export interface HouseLord {
  house: number
  sign: string
  lord: string
  lordHouse: number
}

export interface DivisionalChart {
  type: 'D9' | 'D10'
  planets: PlanetPosition[]
  ascendant: string
}

export interface BirthChartFull extends BirthChart {
  houseLords: HouseLord[]
  divisional: DivisionalChart[]
  nakshatra: string
  pada: number
  lagnaSign: string
}

/** Raw response shape from GET /v1/charts/me and GET /v1/charts/:userId */
interface ChartApiResponse {
  chart: BirthChartFull
  meta: { engine: string; cached: boolean }
}

export interface PanchangData {
  tithi: string
  paksha: string
  nakshatra: string
  yoga: string
  karana: string
  sunriseTime: string
  sunsetTime: string
  moonriseTime: string
  auspiciousnessScore: number
  inauspiciousPeriods: Array<{ name: string; start: string; end: string }>
}

export interface DashboardData {
  panchang: PanchangData
  activeDasha: {
    mahadasha: DashaNode
    antardasha: DashaNode | null
    pratyantardasha: DashaNode | null
    narrative: string
  }
  todayMuhurta: MuhurtaSlot[]
  transitAlerts: Array<{ planet: string; event: string; impact: 'positive' | 'negative' | 'neutral' }>
}

// ── Profile endpoints ───────────────────────────────────────────────────────

export const profileApi = {
  setup: (payload: SetupProfilePayload) =>
    api.patch<User>('/v1/users/me', payload),

  update: (payload: Partial<SetupProfilePayload>) =>
    api.patch<User>('/v1/users/me', payload),
}

// ── Chart response normaliser ────────────────────────────────────────────────
// The backend returns the astro-core BirthChart format whose field names differ
// from the frontend PlanetPosition interface.  This normaliser bridges the gap
// so components can rely on the typed contract without crashing.

const _SIGN_NAMES = [
  'Aries','Taurus','Gemini','Cancer','Leo','Virgo',
  'Libra','Scorpio','Sagittarius','Capricorn','Aquarius','Pisces',
]
const _NAKSHATRA_NAMES = [
  'Ashwini','Bharani','Krittika','Rohini','Mrigashira','Ardra',
  'Punarvasu','Pushya','Ashlesha','Magha','Purva Phalguni','Uttara Phalguni',
  'Hasta','Chitra','Swati','Vishakha','Anuradha','Jyeshtha','Mula',
  'Purva Ashadha','Uttara Ashadha','Shravana','Dhanishtha','Shatabhisha',
  'Purva Bhadrapada','Uttara Bhadrapada','Revati',
]

function _normalisePlanet(p: any, ascSignNum: number): PlanetPosition {
  const signNum: number = typeof p.sign === 'number'
    ? p.sign
    : (_SIGN_NAMES.indexOf(p.sign) + 1) || 1
  return {
    planet:      p.planet ?? p.graha ?? '',
    sign:        typeof p.sign === 'number' ? (_SIGN_NAMES[p.sign - 1] ?? '') : (p.sign ?? ''),
    house:       p.house ?? (((signNum - ascSignNum + 12) % 12) + 1),
    degrees:     p.degrees ?? p.degreeInSign ?? 0,
    isRetrograde: p.isRetrograde ?? false,
    nakshatra:   typeof p.nakshatra === 'number'
      ? (_NAKSHATRA_NAMES[p.nakshatra - 1] ?? '')
      : (p.nakshatra ?? ''),
  }
}

function _normaliseChart(raw: any): BirthChartFull {
  // Resolve ascendant: may be a PlanetaryPosition object or a sign-name string
  const ascObj = raw.ascendant
  const ascSignNum: number =
    ascObj && typeof ascObj === 'object'
      ? (typeof ascObj.sign === 'number'
          ? ascObj.sign
          : (_SIGN_NAMES.indexOf(ascObj.sign) + 1) || 1)
      : (_SIGN_NAMES.indexOf(ascObj) + 1) || 1
  const lagnaSign = raw.lagnaSign ?? _SIGN_NAMES[ascSignNum - 1] ?? ''

  const planets: PlanetPosition[] = (raw.planets ?? []).map(
    (p: any) => _normalisePlanet(p, ascSignNum),
  )

  const moon = planets.find(p => p.planet === 'Moon')
  const sun  = planets.find(p => p.planet === 'Sun')

  const nakshatra = raw.nakshatra
    ?? (typeof raw.moonNakshatra === 'number'
        ? (_NAKSHATRA_NAMES[raw.moonNakshatra - 1] ?? '')
        : (raw.moonNakshatra ?? ''))

  const moonRaw = (raw.planets ?? []).find(
    (p: any) => (p.planet ?? p.graha) === 'Moon',
  )
  const pada = raw.pada ?? moonRaw?.nakshatraPada ?? 1

  return {
    ...raw,
    ascendant:  lagnaSign,
    lagnaSign,
    moonSign:   raw.moonSign  ?? moon?.sign  ?? '',
    sunSign:    raw.sunSign   ?? sun?.sign   ?? '',
    planets,
    dashas:     raw.dashas    ?? [],
    houseLords: raw.houseLords ?? [],
    divisional: raw.divisional ?? [],
    nakshatra,
    pada,
  }
}

// ── VedAstro enrichment types ────────────────────────────────────────────────

/** Per-planet dignity / aspect / Ashtakavarga data from VedAstro */
export interface VedAstroPlanetEnrichment {
  graha: string
  isExalted: boolean
  isDebilitated: boolean
  isInOwnSign: boolean
  isInMoolatrikona: boolean
  hasGoodAspects: boolean
  hasBadAspects: boolean
  isAfflicted: boolean
  isStrong: boolean
  /** Ashtakavarga score in current rashi, 0–8 */
  ashtakavargaScore: number
}

export interface VedAstroEnrichment {
  planets: VedAstroPlanetEnrichment[]
  fetchedAt: string
}

// ── Birth chart endpoints ───────────────────────────────────────────────────

export const chartApi = {
  getMyChart: async (): Promise<BirthChartFull> => {
    const raw = await api.get<ChartApiResponse>('/v1/charts/me')
    return _normaliseChart(raw.chart)
  },

  getChart: async (chartId: string): Promise<BirthChartFull> => {
    const raw = await api.get<ChartApiResponse>(`/v1/charts/${chartId}`)
    return _normaliseChart(raw.chart)
  },

  getInterpretation: (chartId: string) =>
    api.get<ChartInterpretation>(`/v1/charts/${chartId}/interpretation`),

  getEnrichment: async (chartId: string): Promise<VedAstroEnrichment | null> => {
    const res = await api.get<{ enrichment: VedAstroEnrichment | null }>(
      `/v1/charts/${chartId}/enrichment`,
    )
    return res.enrichment
  },
}

// ── Dasha endpoints ─────────────────────────────────────────────────────────

// API response shape from GET /v1/dasha/me (uses `graha`/`antarDashas` naming)
interface DashaApiPeriod {
  graha: string
  startDate: string
  endDate: string
  antarDashas?: DashaApiPeriod[]
}
interface DashaApiResponse {
  periods: DashaApiPeriod[]
}

function _mapDashaPeriod(p: DashaApiPeriod, level: DashaNode['level']): DashaNode {
  const childLevel: DashaNode['level'] = level === 'mahadasha' ? 'antardasha' : 'pratyantardasha'
  return {
    planet: p.graha,
    startDate: p.startDate,
    endDate: p.endDate,
    level,
    children: p.antarDashas?.length
      ? p.antarDashas.map(c => _mapDashaPeriod(c, childLevel))
      : undefined,
  }
}

export const dashaApi = {
  getMyDashas: async (): Promise<DashaNode[]> => {
    const raw = await api.get<DashaApiResponse>('/v1/dasha/me')
    return raw.periods.map(p => _mapDashaPeriod(p, 'mahadasha'))
  },

  getInterpretation: (chartId: string) =>
    api.get<DashaTimeline>(`/v1/dasha/${chartId}/interpretation`),

  getCurrentDasha: (chartId: string) =>
    api.get<CurrentDasha>(`/v1/dasha/${chartId}/current`),
}

// ── Dashboard endpoints ─────────────────────────────────────────────────────

export const dashboardApi = {
  get: () =>
    api.get<DashboardData>('/v1/dashboard'),

  getMuhurta: (date?: string) =>
    api.get<MuhurtaSlot[]>('/v1/muhurta/today', { params: date ? { date } : undefined }),
}

// ── Event muhurta search types ────────────────────────────────────────────────

export interface EventWindowsPayload {
  eventType:      string
  dateRangeStart: string   // ISO 8601 UTC
  dateRangeEnd:   string   // ISO 8601 UTC
  location: {
    latitude:  number
    longitude: number
    timezone:  string
  }
  birthData: {
    birthDateTimeUtc: string
    latitude:         number
    longitude:        number
    timezone:         string
  }
  limit?:          number
  minScore?:       number
  daysOfWeek?:     number[]
  timePreference?: 'morning' | 'afternoon' | 'evening'
}

export interface EventWindowsResponse {
  eventType:          string
  dateRangeStart:     string
  dateRangeEnd:       string
  location:           { latitude: number; longitude: number; timezone: string }
  totalWindowsScored: number
  windows:            MuhurtaWindow[]
  bestWindow?:        MuhurtaWindow
}

// ── Muhurta endpoints ───────────────────────────────────────────────────────

export const muhurtaApi = {
  /** Fetch enriched muhurta windows for a given date (defaults to today). */
  getWindows: (params?: { date?: string; minScore?: number; timePreference?: 'morning' | 'afternoon' | 'evening' }) =>
    api.get<MuhurtaWindow[]>('/v1/muhurta/today', { params }),

  // Bookmarks
  createBookmark: (payload: CreateBookmarkPayload) =>
    api.post<MuhurtaBookmark>('/v1/muhurta/bookmarks', payload),

  getBookmarks: () =>
    api.get<MuhurtaBookmark[]>('/v1/muhurta/bookmarks'),

  deleteBookmark: (id: string) =>
    api.delete<void>(`/v1/muhurta/bookmarks/${id}`),

  /** Returns a URL for direct .ics download — navigate to this, don't fetch. */
  getIcalUrl: (bookmarkId: string) =>
    `${BASE_URL}/v1/muhurta/bookmarks/${bookmarkId}/ical`,

  /** Search enriched muhurta windows across a date range for a specific event type. */
  searchWindows: (payload: EventWindowsPayload) =>
    api.post<EventWindowsResponse>('/v1/muhurta/windows', payload),
}

// ── Choghadiya endpoints ──────────────────────────────────────────────────────

// Raw backend response shape (differs from frontend DailyTimings)
interface DailyTimingsRaw {
  date: string
  sunrise: string
  sunset: string
  daySlots: Array<{ start: string; end: string; label: string; planet: string; period: 'day' | 'night' }>
  nightSlots: Array<{ start: string; end: string; label: string; planet: string; period: 'day' | 'night' }>
  rahuKaal: { start: string; end: string }
  gulikaKaal: { start: string; end: string }
  yamaganda: { start: string; end: string }
}

function transformDailyTimings(raw: DailyTimingsRaw): DailyTimings {
  const mapSlot = (s: DailyTimingsRaw['daySlots'][number]): ChoghadiyaSlot => ({
    startTime: s.start,
    endTime:   s.end,
    quality:   s.label as ChoghadiyaQuality,
    planet:    s.planet,
    period:    s.period,
  })
  const daySlots = Array.isArray(raw?.daySlots) ? raw.daySlots : []
  const nightSlots = Array.isArray(raw?.nightSlots) ? raw.nightSlots : []
  return {
    date:    raw?.date ?? '',
    sunrise: raw?.sunrise ?? '',
    sunset:  raw?.sunset ?? '',
    choghadiya: [...daySlots.map(mapSlot), ...nightSlots.map(mapSlot)],
    inauspiciousWindows: [
      { name: 'Rahu Kaal',   start: raw.rahuKaal.start,   end: raw.rahuKaal.end },
      { name: 'Gulika Kaal', start: raw.gulikaKaal.start, end: raw.gulikaKaal.end },
      { name: 'Yamaganda',   start: raw.yamaganda.start,  end: raw.yamaganda.end },
    ],
  }
}

export const choghadiyaApi = {
  getDailyTimings: async (params: { date: string; lat: number; lon: number; tz: string }): Promise<DailyTimings> => {
    const raw = await api.get<DailyTimingsRaw>('/v1/daily-timings', { params })
    return transformDailyTimings(raw)
  },
}

// ── Compatibility types ─────────────────────────────────────────────────────

export interface AshtakootBreakdown {
  varna:       number  // max 1  — spiritual compatibility
  vashya:      number  // max 2  — dominance/submission
  tara:        number  // max 3  — birth star compatibility
  yoni:        number  // max 4  — sexual compatibility
  grahaMaitri: number  // max 5  — planetary friendship
  gana:        number  // max 6  — temperament
  bhakoot:     number  // max 7  — emotional/financial
  nadi:        number  // max 8  — physical/health
}

export interface AshtakootScore {
  totalPoints:  number
  breakdown:    AshtakootBreakdown
  compatibility: 'excellent' | 'good' | 'average' | 'poor'
  nadiDosha:    boolean
  bhakootDosha: boolean
  ganaDosha:    boolean
}

export interface CompatibilityPartner {
  name:      string
  birthDate: string  // YYYY-MM-DD
  birthTime: string  // HH:MM (local)
  birthPlace: string
  latitude:  number
  longitude: number
}

export interface CompatibilityResult {
  person:  CompatibilityPartner
  partner: CompatibilityPartner
  score:   AshtakootScore
}

// ── Compatibility endpoints ─────────────────────────────────────────────────

/**
 * Convert a local date + time + timezone into an ISO-8601 UTC string
 * suitable for the backend PersonBirthDataDto.birthDateTimeUtc field.
 */
function toUtcIso(date: string, time: string, _tz: string): string {
  // Extract bare date (handles both "YYYY-MM-DD" and full ISO "YYYY-MM-DDT…Z")
  const datePart = date.split('T')[0]
  // Normalise time to HH:MM:SS (handles both "HH:MM" and "HH:MM:SS" from DB)
  const timePart = time.split(':').length >= 3 ? time : `${time}:00`
  return `${datePart}T${timePart}Z`
}

export const compatibilityApi = {
  /**
   * Calculate Ashtakoot guna milan for the logged-in user against a partner.
   * Fetches the current user's profile for person1, builds person2 from partner input,
   * and posts the correctly shaped {person1, person2} payload.
   */
  calculate: async (partner: CompatibilityPartner): Promise<CompatibilityResult> => {
    // Fetch logged-in user's profile for person1 birth data
    const user = await authApi.me()
    const p = user.profile
    if (!p?.dateOfBirth || !p?.timeOfBirth || !p?.birthLatitude || !p?.birthLongitude || !p?.birthTimezone) {
      throw new ApiError(400, 'PROFILE_INCOMPLETE', 'Please complete your birth details in your profile before using Kundali Matching.')
    }

    const person1 = {
      birthDateTimeUtc: toUtcIso(p.dateOfBirth, p.timeOfBirth, p.birthTimezone),
      latitude: p.birthLatitude,
      longitude: p.birthLongitude,
      timezone: p.birthTimezone,
    }

    const person2 = {
      birthDateTimeUtc: toUtcIso(partner.birthDate, partner.birthTime, 'Asia/Kolkata'),
      latitude: partner.latitude,
      longitude: partner.longitude,
      timezone: 'Asia/Kolkata',
    }

    const score = await api.post<AshtakootScore>('/v1/compatibility/ashtakoot', { person1, person2 })

    return {
      person: {
        name: user.displayName,
        birthDate: p.dateOfBirth,
        birthTime: p.timeOfBirth,
        birthPlace: p.birthCity ?? '',
        latitude: p.birthLatitude,
        longitude: p.birthLongitude,
      },
      partner,
      score,
    }
  },
}

// ── Gochar (transit) types ──────────────────────────────────────────────────

export interface GocharTransit {
  planet: string
  event: string
  impact: 'positive' | 'negative' | 'neutral'
  fromSign: string
  toSign?: string
  exactDate: string   // ISO date of exact transit
  description: string
}

// Raw shapes returned by GET /v1/gochar/:userId
interface GocharAspectHit {
  transitGraha: string
  natalGraha: string
  aspectName: string
  transitLongitude: number
  natalLongitude: number
  angleFromTransit: number
  orbDeg: number
  weight: number
}

interface GocharDayScore {
  date: string
  graha: string
  score: number
  isAlert: boolean
  aspects: GocharAspectHit[]
}

interface GocharRawResponse {
  userId: string
  from: string
  to: string
  planet: string
  dailyScores: GocharDayScore[]
  alertDates: string[]
}

// Vedic zodiac signs in order (0° = Aries)
const ZODIAC_SIGNS = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
]

function longitudeToSign(longitude: number): string {
  return ZODIAC_SIGNS[Math.floor(((longitude % 360) + 360) % 360 / 30)]
}

// Natural benefics → positive, natural malefics → negative, Mercury → neutral
const BENEFIC_PLANETS = new Set(['Jupiter', 'Venus', 'Moon'])
const MALEFIC_PLANETS = new Set(['Saturn', 'Mars', 'Rahu', 'Ketu', 'Sun'])

function grahaToImpact(graha: string): 'positive' | 'negative' | 'neutral' {
  if (BENEFIC_PLANETS.has(graha)) return 'positive'
  if (MALEFIC_PLANETS.has(graha)) return 'negative'
  return 'neutral'
}

/**
 * Transform the raw GocharApiResponse into the GocharTransit[] shape the UI expects.
 * Only alert-level scores (score > 70) are surfaced. Per planet, the highest-scoring
 * day is selected so we show one badge per active transit graha.
 */
function transformGocharResponse(raw: GocharRawResponse): GocharTransit[] {
  if (!raw || !Array.isArray(raw.dailyScores)) return []
  // Keep only alert entries, then pick the peak day per graha
  const peakByGraha = new Map<string, GocharDayScore>()
  for (const day of raw.dailyScores) {
    if (!day.isAlert) continue
    const existing = peakByGraha.get(day.graha)
    if (!existing || day.score > existing.score) {
      peakByGraha.set(day.graha, day)
    }
  }

  return Array.from(peakByGraha.values()).map((day): GocharTransit => {
    const topAspect = day.aspects[0]
    const fromSign = topAspect ? longitudeToSign(topAspect.transitLongitude) : 'Unknown'
    const event = topAspect
      ? `${topAspect.aspectName} natal ${topAspect.natalGraha}`
      : 'transit'
    const description = topAspect
      ? `${day.graha} in ${fromSign} forms a ${topAspect.aspectName} with natal ${topAspect.natalGraha} (orb ${topAspect.orbDeg.toFixed(1)}°). Score: ${day.score}/100.`
      : `${day.graha} in ${fromSign} transit. Score: ${day.score}/100.`

    return {
      planet: day.graha,
      event,
      impact: grahaToImpact(day.graha),
      fromSign,
      exactDate: day.date,
      description,
    }
  })
}

// ── Gochar endpoints ────────────────────────────────────────────────────────

export const gocharApi = {
  /** Fetch planetary transits for a user's natal chart over a date range. */
  getTransits: async (userId: string, from?: string, to?: string): Promise<GocharTransit[]> => {
    const today = new Date().toISOString().slice(0, 10)
    const defaultTo = new Date(Date.now() + 90 * 86_400_000).toISOString().slice(0, 10)
    const raw = await api.get<GocharRawResponse>(`/v1/gochar/${userId}`, {
      params: { from: from ?? today, to: to ?? defaultTo },
    })
    return transformGocharResponse(raw)
  },
}

// ── Predictions types ───────────────────────────────────────────────────────

export type LifeArea = 'career' | 'relationships' | 'health' | 'finance' | 'travel' | 'education'

export interface PredictionScore {
  date:  string  // YYYY-MM-DD (first of month)
  score: number  // 0–100
}

export interface PredictionKeyDate {
  date:        string   // YYYY-MM-DD
  type:        'high' | 'low'
  description: string
}

export interface LifeAreaPrediction {
  area:          LifeArea
  currentScore:  number
  trend:         'rising' | 'falling' | 'stable'
  topLayer:      string           // e.g. "Vimshottari Dasha"
  topLayerImpact: 'positive' | 'negative' | 'neutral'
  summary:       string
  scoreTrend:    PredictionScore[]    // 6 monthly points
  keyDates:      PredictionKeyDate[]  // upcoming AI-flagged dates
}

// ── Predictions endpoints ────────────────────────────────────────────────────

export const predictionsApi = {
  /** Fetch all six life-area predictions for the logged-in user. */
  getAll: () =>
    api.get<LifeAreaPrediction[]>('/v1/predictions/me'),

  /** Fetch predictions for a single life area. */
  getArea: (area: LifeArea) =>
    api.get<LifeAreaPrediction>(`/v1/predictions/me/${area}`),
}

// ── Remedies types ───────────────────────────────────────────────────────────

export type RemedyCategory = 'gemstone' | 'mantra' | 'yantra' | 'ritual' | 'charity'
export type RemedyPriority = 'high' | 'medium' | 'low'

export interface Remedy {
  id:            string
  title:         string
  category:      RemedyCategory
  priority:      RemedyPriority
  description:   string
  whenToPerform: string
  duration:      string
  planet?:       string
  purpose:       string
}

// ── Remedies endpoints ───────────────────────────────────────────────────────

const PRIORITY_MAP: Record<number, RemedyPriority> = { 1: 'high', 2: 'medium', 3: 'low' }
const CATEGORY_ALIAS: Record<string, RemedyCategory> = { dana: 'charity', ratna: 'gemstone' }

export const remediesApi = {
  /** Fetch personalised remedy list for the logged-in user. */
  getAll: async (): Promise<Remedy[]> => {
    const raw = await api.get<{
      id: string
      category: string
      remedyText: string
      priority: number
      dashaLord: string | null
      createdAt: string
    }[]>('/v1/remedies/me')

    return raw.map(r => {
      const cat = (CATEGORY_ALIAS[r.category] ?? r.category) as RemedyCategory
      return {
        id: r.id,
        title: r.remedyText,
        category: cat,
        priority: PRIORITY_MAP[r.priority] ?? 'low',
        description: r.remedyText,
        whenToPerform: 'As recommended by your astrologer',
        duration: 'Ongoing',
        planet: r.dashaLord ?? undefined,
        purpose: r.dashaLord ? `Strengthen ${r.dashaLord} influence` : 'Planetary balance',
      }
    })
  },
}

// ── Admin types ──────────────────────────────────────────────────────────────

export interface AdminUser {
  id:            string
  name:          string
  email:         string
  provider:      'google' | 'apple' | 'email' | 'local'
  role:          'user' | 'admin'
  status:        'active' | 'banned'
  createdAt:     string
  lastActiveAt:  string
  birthDate?:    string
  birthPlace?:   string
  activeDasha?:  string
  activityCount: number
  subscription?: AdminUserSubscription | null
}

export interface AdminUserSubscription {
  plan:      'free' | 'premium'
  source:    'stripe' | 'ops_granted' | null
  expiresAt: string | null
}

export interface AdminAuditEntry {
  id:        string
  action:    string
  actorId:   string
  actorName: string
  createdAt: string
  meta?:     Record<string, unknown>
}

export interface AdminUsersResponse {
  users:      AdminUser[]
  nextCursor: string | null
  total:      number
}

// Raw backend response shapes (differ from frontend types)
interface AdminUsersRawResponse {
  items:      RawAdminUser[]
  nextCursor: string | null
  hasMore:    boolean
}

interface RawAdminUser {
  id:          string
  email:       string
  displayName: string | null
  provider:    string
  role:        string
  isActive:    boolean
  createdAt:   string
  updatedAt:   string
  profile?: {
    dateOfBirth?:  string | null
    birthCity?:    string | null
  } | null
  subscription?: {
    plan:                string
    status:              string
    stripeSubscriptionId?: string | null
    currentPeriodEnd?:   string | null
  } | null
}

interface AdminAuditRawResponse {
  items:      RawAuditEntry[]
  nextCursor: string | null
  hasMore:    boolean
}

interface RawAuditEntry {
  id:           string
  adminId:      string
  action:       string
  targetUserId: string
  details:      Record<string, unknown> | null
  createdAt:    string
}

function mapProviderName(provider: string): AdminUser['provider'] {
  if (provider === 'email_otp') return 'email'
  if (provider === 'google' || provider === 'apple' || provider === 'local') return provider
  return 'local'
}

function transformRawUser(raw: RawAdminUser): AdminUser {
  return {
    id:            raw.id,
    name:          raw.displayName ?? raw.email.split('@')[0],
    email:         raw.email,
    provider:      mapProviderName(raw.provider),
    role:          raw.role?.toLowerCase() === 'admin' ? 'admin' : 'user',
    status:        raw.isActive ? 'active' : 'banned',
    createdAt:     raw.createdAt,
    lastActiveAt:  raw.updatedAt,
    birthDate:     raw.profile?.dateOfBirth ?? undefined,
    birthPlace:    raw.profile?.birthCity ?? undefined,
    activityCount: 0,
    subscription:  raw.subscription
      ? {
          plan:      raw.subscription.plan?.toUpperCase() === 'PRO' ? 'premium' : 'free',
          source:    raw.subscription.stripeSubscriptionId ? 'stripe' : 'ops_granted',
          expiresAt: raw.subscription.currentPeriodEnd ?? null,
        }
      : null,
  }
}

function transformRawAuditEntry(raw: RawAuditEntry): AdminAuditEntry {
  return {
    id:        raw.id,
    action:    raw.action.replace(/_/g, ' ').toLowerCase(),
    actorId:   raw.adminId,
    actorName: raw.adminId.slice(0, 8),
    createdAt: raw.createdAt,
    meta:      raw.details ?? undefined,
  }
}

export interface AdminRemedy {
  id:          string
  title:       string
  category:    RemedyCategory
  planet:      string
  priority:    RemedyPriority
  description: string
}

export interface EventWeight {
  eventType: string
  layers:    Record<string, number>  // layer key → weight (0-100, sum = 100)
}

export interface NakshatraQuality {
  nakshatra:  string
  qualities:  Record<string, string>  // event type → quality
}

export interface HealthServiceStatus {
  name:       string
  status:     'ok' | 'degraded' | 'down'
  latencyMs?: number
  message?:   string
}

export interface HealthMetrics {
  latency: {
    p50: { t: string; v: number }[]
    p95: { t: string; v: number }[]
    p99: { t: string; v: number }[]
  }
  errorRate:    { t: string; v: number }[]
  cacheHitRate: number
  queueDepth:   number
  dau:          number
  mau:          number
  uptime:       number
  lastUpdated:  string
  /** Optional per-service health breakdown */
  services?:    HealthServiceStatus[]
}

// ── Admin endpoints ───────────────────────────────────────────────────────────

export const adminApi = {
  // Users — transforms backend {items,nextCursor,hasMore} → frontend {users,nextCursor,total}
  getUsers: async (params?: {
    q?: string
    provider?: string
    status?: string
    plan?: string
    cursor?: string
    limit?: number
  }): Promise<AdminUsersResponse> => {
    const search = new URLSearchParams()
    if (params?.q)        search.set('q', params.q)
    if (params?.provider && params.provider !== 'all') {
      // Map frontend provider names to backend enum values
      const providerMap: Record<string, string> = { email: 'email_otp', google: 'google', apple: 'apple', local: 'local' }
      search.set('provider', providerMap[params.provider] ?? params.provider)
    }
    if (params?.status && params.status !== 'all') {
      // Backend uses isActive boolean, not status string
      search.set('isActive', params.status === 'active' ? 'true' : 'false')
    }
    if (params?.plan && params.plan !== 'all') {
      // Backend uses SubscriptionPlan enum: 'free' | 'pro' | 'business'
      const planMap: Record<string, string> = { free: 'free', premium: 'pro' }
      search.set('plan', planMap[params.plan] ?? params.plan)
    }
    if (params?.cursor)  search.set('cursor', params.cursor)
    if (params?.limit)   search.set('limit', String(params.limit))
    const qs = search.toString()
    const raw = await api.get<AdminUsersRawResponse>(`/v1/admin/users${qs ? `?${qs}` : ''}`)
    return {
      users:      raw.items.map(transformRawUser),
      nextCursor: raw.nextCursor,
      total:      raw.items.length,
    }
  },
  setUserStatus: (id: string, action: 'ban' | 'unban') =>
    api.post<void>(`/v1/admin/users/${id}/${action}`, {}),
  forceLogout: (id: string) =>
    api.post<void>(`/v1/admin/users/${id}/force-logout`, {}),
  deleteUser: (id: string) =>
    api.delete<void>(`/v1/admin/users/${id}`),
  grantPremium: (id: string) =>
    api.post<void>(`/v1/admin/users/${id}/grant-premium`, {}),
  revokePremium: (id: string) =>
    api.post<void>(`/v1/admin/users/${id}/revoke-premium`, {}),
  getAuditLog: async (targetUserId: string): Promise<AdminAuditEntry[]> => {
    const raw = await api.get<AdminAuditRawResponse>(`/v1/admin/audit-log?targetUserId=${targetUserId}`)
    return raw.items.map(transformRawAuditEntry)
  },

  // Remedy library
  getRemedies: () =>
    api.get<AdminRemedy[]>('/v1/admin/remedies'),
  createRemedy: (body: Omit<AdminRemedy, 'id'>) =>
    api.post<AdminRemedy>('/v1/admin/remedies', body),
  updateRemedy: (id: string, body: Partial<Omit<AdminRemedy, 'id'>>) =>
    api.patch<AdminRemedy>(`/v1/admin/remedies/${id}`, body),
  deleteRemedy: (id: string) =>
    api.delete<void>(`/v1/admin/remedies/${id}`),

  // Event weights
  getEventWeights: () =>
    api.get<EventWeight[]>('/v1/admin/config/event-weights'),
  saveEventWeights: (weights: EventWeight[]) =>
    api.put<EventWeight[]>('/v1/admin/config/event-weights', weights),

  // Nakshatra table
  getNakshatraTable: () =>
    api.get<NakshatraQuality[]>('/v1/admin/config/nakshatra'),
  saveNakshatraTable: (table: NakshatraQuality[]) =>
    api.put<NakshatraQuality[]>('/v1/admin/config/nakshatra', table),

  // Health
  getHealth: () =>
    api.get<HealthMetrics>('/v1/admin/health'),
}

// ── Developer API types ───────────────────────────────────────────────────────

export type ApiKeyStatus = 'active' | 'revoked'
export type DeveloperTier = 'free' | 'starter' | 'pro' | 'enterprise'

export interface ApiKey {
  id:          string
  name:        string
  prefix:      string   // first 8 chars shown, e.g. "sk_live_"
  status:      ApiKeyStatus
  createdAt:   string
  lastUsedAt:  string | null
  callCount:   number
}

export interface ApiKeyCreated extends ApiKey {
  secret: string  // full key — returned only once on creation
}

export interface UsageDataPoint {
  date:       string  // YYYY-MM-DD
  calls:      number
  errors:     number
}

export interface EndpointUsage {
  endpoint:   string
  calls:      number
  avgLatencyMs: number
  errorRate:  number
}

export interface DeveloperUsage {
  period:      string  // e.g. "last_30_days"
  totalCalls:  number
  totalErrors: number
  dailySeries: UsageDataPoint[]
  topEndpoints: EndpointUsage[]
}

export interface DeveloperAccount {
  tier:           DeveloperTier
  callsThisMonth: number
  callLimit:      number  // -1 = unlimited
  resetDate:      string  // ISO date
  webhookUrl:     string | null
}

// ── Notifications types ───────────────────────────────────────────────────────

export interface NotificationPreferences {
  morningSummary:     boolean
  morningSummaryTime: string   // HH:mm, default '06:30'
  rahuKaalAlert:      boolean
  abhijitMuhurta:     boolean
  nakshatraChange:    boolean
  quietHoursEnabled:  boolean
  quietHoursStart:    string   // HH:mm
  quietHoursEnd:      string   // HH:mm
}

export interface PushSubscriptionPayload {
  endpoint: string
  p256dh:   string
  auth:     string
}

// ── Notifications endpoints ───────────────────────────────────────────────────

export const notificationsApi = {
  subscribe: (payload: PushSubscriptionPayload) =>
    api.post<void>('/v1/notifications/subscribe', payload),

  unsubscribe: () =>
    api.delete<void>('/v1/notifications/subscribe'),

  getPreferences: () =>
    api.get<NotificationPreferences>('/v1/notifications/preferences'),

  updatePreferences: (prefs: Partial<NotificationPreferences>) =>
    api.patch<NotificationPreferences>('/v1/notifications/preferences', prefs),
}

// ── Billing types ─────────────────────────────────────────────────────────────

export type BillingPlanId =
  | 'premium_monthly'
  | 'premium_annual'
  | 'api_developer'
  | 'api_business'

export type FeatureKey =
  | 'muhurta'        // usage-gated: 3/month free
  | 'compatibility'  // hard gate: premium only
  | 'dasha_full'     // soft gate: preview free, full premium
  | 'remedies'       // hard gate: premium only
  | 'insights'       // hard gate: premium only
  | 'event_muhurta'  // hard gate: premium only

/** Structured 403 error body returned by FeatureGateGuard on the backend. */
export interface FeatureGatedError {
  code: 'FEATURE_GATED'
  feature: FeatureKey
  requiredPlan: 'free' | 'premium'
  remainingUses?: number
}

export interface CheckoutPayload {
  planId: BillingPlanId
  trial?: boolean
}

export interface CheckoutResponse {
  checkoutUrl: string
}

export interface BillingPortalResponse {
  portalUrl: string
}

/** Rich billing subscription info for the BillingPage (extends Subscription). */
export type SubscriptionState = 'free' | 'trialing' | 'active' | 'past_due' | 'cancelled'

export interface BillingSubscription {
  state: SubscriptionState
  planId: BillingPlanId | null
  price: string | null          // e.g. "$9.99 / month"
  nextBillingDate: string | null
  trialEndsAt: string | null
  cancelAtPeriodEnd: boolean
}

export interface BillingUsage {
  used: number
  limit: number   // 0 = unlimited
  period: string  // e.g. "March 2026"
  resetDate: string | null
}

export interface BillingUsageResponse {
  portalUrl: string
}

// ── Billing endpoints ─────────────────────────────────────────────────────────

export const billingApi = {
  /** Initiate a Stripe checkout session. Redirects to checkoutUrl on success. */
  checkout: (payload: CheckoutPayload) =>
    api.post<CheckoutResponse>('/v1/billing/checkout', payload),

  /** Get the current user's subscription status and usage. */
  getSubscription: async (): Promise<BillingSubscription> => {
    const raw = await api.get<{
      plan: string
      status: string
      cancelAtPeriodEnd: boolean
      currentPeriodEnd: string | null
      trialEnd: string | null
      trialUsed: boolean
    }>('/v1/billing/subscription')

    // Map backend status to frontend state
    const stateMap: Record<string, BillingSubscription['state']> = {
      active: raw.plan === 'free' ? 'free' : 'active',
      trialing: 'trialing',
      past_due: 'past_due',
      canceled: 'cancelled',
      cancelled: 'cancelled',
    }

    return {
      state: stateMap[raw.status] ?? 'free',
      planId: raw.plan === 'pro' ? 'premium_monthly' : null,
      price: null,
      nextBillingDate: raw.currentPeriodEnd,
      trialEndsAt: raw.trialEnd,
      cancelAtPeriodEnd: raw.cancelAtPeriodEnd,
    }
  },

  /** Get API request usage for the current billing period. */
  getUsage: async (): Promise<BillingUsage> => {
    const raw = await api.get<{
      requests: number
      limit: number
      remaining: number
      plan: string
    }>('/v1/billing/usage')

    const now = new Date()
    const period = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    const resetDate = new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString()

    return {
      used: raw.requests,
      limit: raw.limit,
      period,
      resetDate,
    }
  },

  /** Open the Stripe billing portal (returns redirect URL). */
  portal: () =>
    api.post<BillingPortalResponse>('/v1/billing/portal', {}),

  /** @deprecated use portal() */
  getPortalUrl: () =>
    api.post<BillingPortalResponse>('/v1/billing/portal', {}),
}

// ── Developer endpoints ───────────────────────────────────────────────────────

export const developerApi = {
  // API keys
  listKeys: () =>
    api.get<ApiKey[]>('/v1/api-keys'),
  createKey: (name: string) =>
    api.post<ApiKeyCreated>('/v1/api-keys', { name }),
  revokeKey: (id: string) =>
    api.delete<void>(`/v1/api-keys/${id}`),

  // Usage (per-key)
  getUsage: (period?: '7d' | '30d' | '90d') =>
    api.get<DeveloperUsage>('/v1/api-keys/usage', { params: period ? { period } : undefined }),

  // Account
  getAccount: () =>
    api.get<DeveloperAccount>('/v1/api-keys/account'),
}
